<template>
  <div class="middle-box text-center loginscreen animated fadeInDown">
    <div>
      <div>
        <h1 class="logo-name">CP</h1>
      </div>

      <h3>Witam w {{ brandName }}</h3>

      <p>
        <span>Zaloguj się przy pomocy</span>
        <a class="login-with-barcode"> kodu kreskowego</a>
      </p>

      <form class="m-t" role="form" novalidate @submit.prevent="login">
        <div class="has-error">
          <span
            v-for="(error, index) in serverErrors.nonFieldErrors"
            :key="index"
            class="help-block"
          >
            {{ error }}
          </span>
        </div>

        <div class="form-group">
          <input
            v-model="username"
            @input="dirty.username = true"
            id="username"
            name="username"
            type="text"
            class="form-control"
            placeholder="Nazwa użytkownika"
            autocomplete="username"
          />

          <div class="has-error">
            <span v-if="dirty.username && !username" class="help-block">
              To pole nie może być puste.
            </span>
            <span
              v-for="(error, index) in serverErrors.username"
              :key="index"
              class="help-block"
            >
              {{ error }}
            </span>
          </div>
        </div>

        <div
          class="form-group"
          :class="{ 'has-error': dirty.password && !password }"
        >
          <input
            v-model="password"
            @input="dirty.password = true"
            id="password"
            name="password"
            type="password"
            class="form-control"
            placeholder="Hasło"
            autocomplete="current-password"
          />

          <div class="has-error">
            <span v-if="dirty.password && !password" class="help-block">
              To pole nie może być puste.
            </span>
            <span
              v-for="(error, index) in serverErrors.password"
              :key="index"
              class="help-block"
            >
              {{ error }}
            </span>
          </div>
        </div>

        <button
          type="submit"
          class="btn btn-primary block full-width m-b"
          :disabled="!isValid || sending"
        >
          Zaloguj się
        </button>
      </form>

      <p class="m-t">
        <small>{{ brandName }} &copy; 2024</small>
      </p>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { useCookies } from "vue3-cookies";
import router from "../../router/router";
import { getBrandNameFromCurrentDomain } from "../../site-domain";
import { setUser } from "../../permissions";

const CONNECTION_ERROR = "Nie udało się połączyć z serwerem.";
const ADMIN_NOT_FOUND_ERROR = "Nie znaleziono administratora dla tego konta.";

const asList = (value) => {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
};

const emptyErrors = () => ({
  username: [],
  password: [],
  nonFieldErrors: [],
});

// Works like the login form of the old admin panel: Enter submits the form,
// the button stays disabled until both fields are filled, and the API errors
// are shown under their fields - the general ones (wrong credentials, an IP
// that may not log in) above the form.
export default {
  name: "LoginPage",

  setup() {
    const store = useStore();
    const { cookies } = useCookies();
    const brandName = ref(getBrandNameFromCurrentDomain());
    const username = ref("");
    const password = ref("");
    // A field shows its "required" error only after it was typed in.
    const dirty = ref({ username: false, password: false });
    const serverErrors = ref(emptyErrors());
    const sending = ref(false);

    const isValid = computed(() => !!username.value && !!password.value);

    const showErrors = (data) => {
      serverErrors.value = {
        username: asList(data?.username),
        password: asList(data?.password),
        nonFieldErrors: asList(data?.detail).concat(
          asList(data?.non_field_errors)
        ),
      };
      if (
        !serverErrors.value.username.length &&
        !serverErrors.value.password.length &&
        !serverErrors.value.nonFieldErrors.length
      ) {
        serverErrors.value.nonFieldErrors = [CONNECTION_ERROR];
      }
    };

    const storeAdmin = (user) => {
      cookies.set("CPadmin", user.first_name + " " + user.last_name);
      cookies.set("CPid", parseInt(user.pk));
      cookies.set("CPusername", user.username);
      setUser(user);
      sessionStorage.setItem("showUnsentInvoicesModal", "1");
      router.push({
        name: "Home",
        path: "/",
      });
    };

    const login = async () => {
      if (!isValid.value || sending.value) {
        return;
      }
      serverErrors.value = emptyErrors();
      sending.value = true;
      try {
        const response = await store.dispatch("login", {
          username: username.value,
          password: password.value,
        });
        if (!response?.data?.token) {
          showErrors(response?.data);
          return;
        }
        cookies.set("CPsession", response.data.token);

        const adminResponse = await store.dispatch("getAdminByUsername", {
          username: username.value,
        });
        if (!adminResponse?.data?.user) {
          // The token alone is not enough - the panel needs the admin data
          // (permissions), so a client account cannot get into it.
          cookies.remove("CPsession");
          serverErrors.value.nonFieldErrors = [ADMIN_NOT_FOUND_ERROR];
          return;
        }
        storeAdmin(adminResponse.data.user);
      } catch (error) {
        showErrors(null);
      } finally {
        sending.value = false;
      }
    };

    return {
      brandName,
      username,
      password,
      dirty,
      serverErrors,
      sending,
      isValid,
      login,
    };
  },
};
</script>
